"use client";

import { useEffect, useState, type ReactNode } from "react";
import { hasAdClickId } from "@/lib/analytics/googleAds";

// Marketing/analytics tags and other non-critical SDKs account for a large
// share of page weight. Loading them eagerly starves the LCP hero image and
// main thread on mobile, so consumers of this hook wait for the first sign of
// user engagement, with a timer fallback so non-interacting visitors are
// still covered.
const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel", "mousemove"] as const;
const FALLBACK_DELAY_MS = 12000;

export function useDeferredReady(): boolean {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (ready) return undefined;

        // Paid-click landings (gclid etc.) must tag immediately: a visitor who
        // bounces before interacting would otherwise never register a page_view
        // or remarketing hit, and the ad platform loses the click entirely.
        if (hasAdClickId(window.location.search)) {
            setReady(true);
            return undefined;
        }

        const activate = () => {
            INTERACTION_EVENTS.forEach((eventName) => window.removeEventListener(eventName, activate));
            window.clearTimeout(timerId);
            setReady(true);
        };

        INTERACTION_EVENTS.forEach((eventName) =>
            window.addEventListener(eventName, activate, { once: true, passive: true })
        );
        const timerId = window.setTimeout(activate, FALLBACK_DELAY_MS);

        return () => {
            INTERACTION_EVENTS.forEach((eventName) => window.removeEventListener(eventName, activate));
            window.clearTimeout(timerId);
        };
    }, [ready]);

    return ready;
}

export default function DeferThirdParty({ children }: { children: ReactNode }) {
    const ready = useDeferredReady();

    return ready ? <>{children}</> : null;
}
